import { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../../../config/config';
import handleOpenSweetAlert from '../../handleOpenSweetAlert';
import { useSession } from '../../hooks/useSession';

export const useModalPagoMasivoController = (open, onClose, selectedRows, onSaved) => {
  const apiUrl = config.apiBaseUrl;
  const { loggedInUsername, idEmpresa } = useSession();

  const [rowsToProcess, setRowsToProcess] = useState([]);
  const [pagosList, setPagosList] = useState({});
  const [enviarCorreos, setEnviarCorreos] = useState(false);
  const [metodosPago, setMetodosPago] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [combinedErrors, setCombinedErrors] = useState({});

  useEffect(() => {
    if (open && selectedRows?.length) {
      setRowsToProcess([...selectedRows]);
      const initial = {};
      selectedRows.forEach(r => {
        initial[r.ID] = {
          fechaPago: new Date().toISOString().split('T')[0],
          metodoPago: '',
          referencia: '',
          observaciones: '',
          files: [],
          referenciasArray: ['']
        };
      });
      setPagosList(initial);
      setEnviarCorreos(false);
      setProgress({ current: 0, total: 0 });
      setCombinedErrors({});
    }
  }, [open, selectedRows]);

  useEffect(() => {
    if (open && !metodosPago.length) {
      fetch(`${apiUrl}/api/metodospago`)
        .then(res => res.json())
        .then(data => {
            setMetodosPago(data || []);
        })
        .catch(err => console.error('Error fetching metodos pago', err));
    }
  }, [open, metodosPago.length, apiUrl]);

  const handleRowChange = (id, field, value) => {
    setPagosList(p => ({
      ...p,
      [id]: { ...p[id], [field]: value }
    }));
    
    // Limpiar error de este campo
    setCombinedErrors(prev => {
       const u = { ...prev };
       if (u[id]) {
           const rowErrs = { ...u[id] };
           delete rowErrs[field];
           u[id] = rowErrs;
       }
       return u;
    });

    if (field === 'metodoPago') {
      const met = metodosPago.find(m => String(m.METODOPAGOID) === String(value));
      const label = (met?.METODOPAGO || '').toUpperCase();
      
      const isTransfer = label.includes('TRANSFERENCIA');
      const isOptional = label.includes('EFECTIVO') || label.includes('REFERENCIA') || label.includes('TARJETA');
      
      let reqRef = Number(met?.REQUIERE_REFERENCIA) === 1;
      let reqComp = Number(met?.REQUIERE_COMPROBANTE) === 1 || isTransfer || isOptional;

      if (!reqComp) {
          setPagosList(p => ({ ...p, [id]: { ...p[id], files: [] } }));
      }
      if (!reqRef) {
          setPagosList(p => ({ ...p, [id]: { ...p[id], referencia: '', referenciasArray: [''] } }));
      }
    }

    if (field === 'referencia') {
        const refs = value.split(';').map(x => x.trim()).filter(x => x !== '');
        setPagosList(p => ({
            ...p,
            [id]: { 
                ...p[id], 
                referenciasArray: refs.length ? refs : [''] 
            }
        }));
    }
  };

  const handleRowFiles = (id, newFilesList) => {
    setPagosList(p => {
      const arr = Array.from(newFilesList);
      const existing = p[id]?.files || [];
      // Combine avoiding identical files by name and size
      const existingSet = new Set(existing.map(f => `${f.name}__${f.size}`));
      const nonDupes = arr.filter(f => !existingSet.has(`${f.name}__${f.size}`));
      return { ...p, [id]: { ...p[id], files: [...existing, ...nonDupes] } };
    });
    setCombinedErrors(prev => {
       const u = { ...prev };
       if (u[id]) {
           const rowErrs = { ...u[id] };
           delete rowErrs.files;
           u[id] = rowErrs;
       }
       return u;
    });
  };

  const removeRowFile = (id, fileIdx) => {
    setPagosList(p => {
      const existing = p[id]?.files || [];
      return { ...p, [id]: { ...p[id], files: existing.filter((_, i) => i !== fileIdx) } };
    });
  };

  const handleRowReferenciaArrayChange = (id, index, value) => {
    setPagosList(p => {
      const rowData = p[id];
      if (!rowData) return p;
      const nuevoArray = [...(rowData.referenciasArray || [''])];
      nuevoArray[index] = value;
      return {
        ...p,
        [id]: {
          ...rowData,
          referenciasArray: nuevoArray,
          referencia: nuevoArray.filter(x => x.trim()).join('; ')
        }
      };
    });
    setCombinedErrors(prev => {
       const u = { ...prev };
       if (u[id]) {
           const rowErrs = { ...u[id] };
           delete rowErrs.referencia;
           u[id] = rowErrs;
       }
       return u;
    });
  };

  const addRowReferenciaField = (id) => {
    setPagosList(p => {
      const rowData = p[id];
      if (!rowData) return p;
      return {
        ...p,
        [id]: {
          ...rowData,
          referenciasArray: [...(rowData.referenciasArray || ['']), '']
        }
      };
    });
  };

  const removeRowReferenciaField = (id, index) => {
    setPagosList(p => {
      const rowData = p[id];
      if (!rowData) return p;
      const nuevoArray = (rowData.referenciasArray || ['']).filter((_, i) => i !== index);
      const final = nuevoArray.length ? nuevoArray : [''];
      return {
        ...p,
        [id]: {
          ...rowData,
          referenciasArray: final,
          referencia: final.filter(x => x.trim()).join('; ')
        }
      };
    });
  };

  const normalizeEmpresaArray = (id) => { if (Array.isArray(id)) return id; if (typeof id === 'string') return id.split(',').map(Number); return [Number(id)].filter(x => x); };

  const autorizarPagosMasivos = async () => {
    if (!validate()) return;
    
    setLoading(true);
    setProgress({ current: 0, total: rowsToProcess.length });

    let exitoCount = 0;
    let errorCount = 0;
    const successfulIds = [];

    for (let i = 0; i < rowsToProcess.length; i++) {
      const row = rowsToProcess[i];
      const data = pagosList[row.ID];
      setProgress({ current: i + 1, total: rowsToProcess.length });

      try {
        const formData = new FormData();
        formData.append('fechaPago', data.fechaPago || '');
        formData.append('metodoPago', data.metodoPago || '');
        formData.append('referencia', data.referencia || '');
        formData.append('observaciones', data.observaciones || '');
        formData.append('AUT', 1);
        formData.append('MONTOSOLICITADO1', row.MONTOSOLICITADO || '');
        formData.append('FOLIO1', row.FOLIO || '');
        formData.append('CENTROCOSTO1', row.CENTROCOSTO || '');
        formData.append('DEPARTAMENTO1', row.DEPARTAMENTO || '');
        formData.append('SOLICITANTE1', row.SOLICITANTE || '');
        formData.append('ID1', row.ID);
        formData.append('nombreEmpresa', row.EMPRESA || '');
        formData.append('idEmpresa', JSON.stringify(normalizeEmpresaArray(idEmpresa)));
        formData.append('loggedInUsername', loggedInUsername || '');
        
        formData.append('skipEmail', !enviarCorreos);

        if (data.files && data.files.length > 0) {
            data.files.forEach(f => formData.append('adjunto', f));
        }

        await axios.put(`${apiUrl}/tesoreroProcesarPago`, formData);
        exitoCount++;
        successfulIds.push(row.ID);
      } catch (err) {
        console.error(`Error procesando OC ${row.FOLIO}:`, err);
        errorCount++;
      }
    }

    setLoading(false);
    
    // Update the local list to remove successfully processed rows
    setRowsToProcess(prev => prev.filter(r => !successfulIds.includes(r.ID)));

    if (errorCount === 0) {
        handleOpenSweetAlert(`Se registraron ${exitoCount} pagos exitosamente.`, 'success');
        onClose();
    } else {
        handleOpenSweetAlert(`Proceso completado con ${errorCount} errores. (${exitoCount} exitosos)`, 'warning');
    }
    
    if (exitoCount > 0 && onSaved) onSaved();
  };

  const validate = () => {
    const errs = {};
    let hasError = false;

    selectedRows.forEach(row => {
        const data = pagosList[row.ID];
        if (!data) return;
        
        const rowErrs = {};
        const met = metodosPago.find(m => String(m.METODOPAGOID) === String(data.metodoPago));
        if (!met) {
            if (!data.metodoPago) rowErrs.metodoPago = 'Req.';
        } else {
            const label = (met?.METODOPAGO || '').toUpperCase();

            const isTransfer = label.includes('TRANSFERENCIA');
            const isOptional = label.includes('EFECTIVO') || label.includes('REFERENCIA') || label.includes('TARJETA');
            
            let reqRef = Number(met?.REQUIERE_REFERENCIA) === 1;
            let reqComp = Number(met?.REQUIERE_COMPROBANTE) === 1 || isTransfer || isOptional;
            const compMandatory = isTransfer;

            if (!data.fechaPago) rowErrs.fechaPago = 'Req.';
            if (!data.metodoPago) rowErrs.metodoPago = 'Req.';
            if (reqRef && !data.referencia) rowErrs.referencia = 'Req.';
            
            if (reqComp && compMandatory && (!data.files || data.files.length === 0)) {
                rowErrs.files = 'Falta archivo (Mandatorio p/ transferencia)';
            }
        }

        if (Object.keys(rowErrs).length > 0) {
            errs[row.ID] = rowErrs;
            hasError = true;
        }
    });

    setCombinedErrors(errs);
    if (hasError) {
        handleOpenSweetAlert('Hay errores o información faltante en una o más filas. Revise el formulario.', 'error');
    }
    return !hasError;
  };

  return {
    rowsToProcess, setRowsToProcess, pagosList, handleRowChange, handleRowFiles, removeRowFile, metodosPago, enviarCorreos, setEnviarCorreos,
    combinedErrors, loading, progress, autorizarPagosMasivos,
    handleRowReferenciaArrayChange, addRowReferenciaField, removeRowReferenciaField
  };
};
